/**
 * ניהול קטגוריות — TASKS 3.2.
 *
 * ⚠️ **שינוי סדר: לחצני מעלה/מטה, ⛔ לא גרירה.** TASKS מבקש "גרירה + חלופת
 *    מקלדת", אבל גרירת מגע אמינה דורשת ספריית dnd וטיפול נגישות משלה
 *    (aria-live על כל שינוי מיקום, טיפול מגע ל-iOS/Android). ברשימה קטנה
 *    (קטגוריות בודדות) לחצנים משיגים בדיוק את אותה תוצאה, עובדים זהה בעכבר/
 *    מגע/מקלדת בלי תלות נוספת, ואמינים יותר על מובייל. ⭐ ניתן להוסיף גרירה
 *    אמיתית מעליהם בעתיד בלי לשבור את ה-API (`reorderCategories` כבר עצמאי
 *    ממנגנון הקלט).
 */
import { useEffect, useRef, useState, type FormEvent } from 'react';
import { ArrowDown, ArrowUp, Image as ImageIcon, Trash, UploadSimple } from '@phosphor-icons/react';
import { AdminNav } from './AdminNav.js';
import { AdminPageHeader } from './AdminPageHeader.js';
import {
  type Category,
  createCategory,
  deleteCategory,
  listCategories,
  reorderCategories,
  suggestCategory,
  updateCategory,
  uploadCategoryImage,
} from './categoriesApi.js';
import { isAdminApiError } from './adminApi.js';

export function CategoriesPage() {
  const [categories, setCategories] = useState<Category[] | null>(null);
  const [listError, setListError] = useState<string | null>(null);

  const [name, setName] = useState('');
  const [prefix, setPrefix] = useState('');
  // ⭐ ה-slug (כתובת ה-URL הציבורית) אף פעם לא מוצג/נערך ידנית על ידי טל -
  // לא אינפורמטיבי עבורו. נגזר אוטומטית מהשם, במקביל לקידומת המק"ט.
  const [slug, setSlug] = useState('');
  const [prefixHint, setPrefixHint] = useState<string | null>(null);
  const [prefixTouched, setPrefixTouched] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const [confirmDeleteId, setConfirmDeleteId] = useState<number | null>(null);
  const [rowError, setRowError] = useState<string | null>(null);
  const [statusMessage, setStatusMessage] = useState<string | null>(null);
  const [uploadingImageId, setUploadingImageId] = useState<number | null>(null);

  const suggestTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  async function refetch() {
    try {
      setCategories(await listCategories());
    } catch (err) {
      setListError(isAdminApiError(err) ? err.message : 'שגיאה בטעינת הקטגוריות.');
    }
  }

  useEffect(() => {
    void refetch();
  }, []);

  useEffect(() => {
    if (suggestTimer.current) clearTimeout(suggestTimer.current);
    // ⭐ ה-slug תמיד נגזר אוטומטית (⛔ אין דרך לטל לערוך אותו) - ולכן ההצעה
    // ממשיכה לרוץ גם אחרי ש-prefix נערך ידנית, כדי לשמור את ה-slug מעודכן.
    if (name.trim().length === 0) return;

    suggestTimer.current = setTimeout(() => {
      suggestCategory(name)
        .then((result) => {
          if (!prefixTouched) setPrefix(result.prefix);
          setSlug(result.slug);
          setPrefixHint(
            result.prefixWasAdjusted ? 'הקידומת הראשונית הייתה תפוסה - הוצעה חלופה פנויה.' : null,
          );
        })
        .catch(() => {
          // ⭐ הצעה אוטומטית שנכשלת ⛔ לא חוסמת — טל תמיד יכול להקליד קידומת ידנית
        });
    }, 400);

    return () => {
      if (suggestTimer.current) clearTimeout(suggestTimer.current);
    };
    // ⚠️ prefixTouched **חייב** להיות בתלות, ⛔ לא רק להיבדק בגוף ה-effect:
    //    אחרת ה-closure בתוך ה-setTimeout קופא על הערך שהיה ב-render שבו name
    //    השתנה לאחרונה, וההצעה עלולה לדרוס שדה שטל כבר ערך ידנית **אחרי**
    //    שה-timer כבר נקבע. השמת הדגל כתלות גורמת ל-effect לרוץ מחדש בכל
    //    שינוי, לנקות את ה-timer הישן ולבדוק את השער מחדש עם הערך העדכני.
  }, [name, prefixTouched]);

  async function handleCreate(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setFormError(null);
    setSubmitting(true);
    try {
      await createCategory({ name: name.trim(), slug: slug.trim(), skuPrefix: prefix.trim() });
      setName('');
      setPrefix('');
      setSlug('');
      setPrefixTouched(false);
      setPrefixHint(null);
      setStatusMessage('הקטגוריה נוספה.');
      await refetch();
    } catch (err) {
      setFormError(isAdminApiError(err) ? err.message : 'שגיאה ביצירת הקטגוריה.');
    } finally {
      setSubmitting(false);
    }
  }

  async function handleToggleActive(category: Category) {
    setRowError(null);
    try {
      await updateCategory(category.id, { isActive: !category.is_active });
      await refetch();
    } catch (err) {
      setRowError(isAdminApiError(err) ? err.message : 'שגיאה בעדכון הקטגוריה.');
    }
  }

  async function handleMove(index: number, direction: -1 | 1) {
    if (!categories) return;
    const targetIndex = index + direction;
    if (targetIndex < 0 || targetIndex >= categories.length) return;

    const reordered = [...categories];
    const moved = reordered[index] as Category;
    const swapped = reordered[targetIndex] as Category;
    reordered[index] = swapped;
    reordered[targetIndex] = moved;

    setCategories(reordered); // ⭐ עדכון אופטימי — מרגיש מיידי
    setRowError(null);
    try {
      await reorderCategories(reordered.map((c) => c.id));
      setStatusMessage(`הסדר עודכן - "${moved.name}" הוזזה.`);
    } catch (err) {
      setRowError(isAdminApiError(err) ? err.message : 'שגיאה בשינוי הסדר.');
      await refetch(); // ⛔ נכשל בשרת — חוזרים למצב האמיתי
    }
  }

  async function handleImageUpload(categoryId: number, file: File) {
    setRowError(null);
    setUploadingImageId(categoryId);
    try {
      await uploadCategoryImage(categoryId, file);
      setStatusMessage('תמונת הקטגוריה עודכנה.');
      await refetch();
    } catch (err) {
      setRowError(isAdminApiError(err) ? err.message : 'שגיאה בהעלאת תמונת הקטגוריה.');
    } finally {
      setUploadingImageId(null);
    }
  }

  async function handleDelete(id: number) {
    setRowError(null);
    try {
      await deleteCategory(id);
      setConfirmDeleteId(null);
      setStatusMessage('הקטגוריה נמחקה.');
      await refetch();
    } catch (err) {
      setRowError(isAdminApiError(err) ? err.message : 'שגיאה במחיקת הקטגוריה.');
    }
  }

  return (
    <>
      <AdminNav />
      <main className="mx-auto max-w-3xl px-6 py-12">
        <AdminPageHeader
          title="קטגוריות"
          description="ניהול קטגוריות הקטלוג וסדר הצגתן באתר הציבורי."
        />

        <div aria-live="polite" className="sr-only">
          {statusMessage}
        </div>

        <section
          className="border-line bg-surface mt-6 rounded-lg border p-6"
          aria-labelledby="new-cat-h"
        >
          <h2 id="new-cat-h" className="text-lg font-semibold">
            קטגוריה חדשה
          </h2>

          <form
            className="mt-4 grid gap-4 sm:grid-cols-[2fr_1fr_auto]"
            onSubmit={(e) => void handleCreate(e)}
          >
            <div>
              <label htmlFor="cat-name" className="block text-sm font-semibold">
                שם
              </label>
              <input
                id="cat-name"
                required
                aria-required="true"
                value={name}
                onChange={(e) => setName(e.target.value)}
                className="mt-1 w-full rounded-md border border-line px-3 py-2"
              />
            </div>

            <div>
              <label htmlFor="cat-prefix" className="block text-sm font-semibold">
                קידומת מק"ט
              </label>
              <input
                id="cat-prefix"
                required
                aria-required="true"
                aria-describedby={prefixHint ? 'cat-prefix-hint' : undefined}
                maxLength={2}
                value={prefix}
                onChange={(e) => {
                  setPrefixTouched(true);
                  setPrefix(e.target.value.toUpperCase());
                }}
                className="ltr-inline mt-1 w-full rounded-md border border-line px-3 py-2 uppercase"
              />
              {prefixHint && (
                <p id="cat-prefix-hint" className="text-muted mt-1 text-xs">
                  {prefixHint}
                </p>
              )}
            </div>

            <div className="flex items-end">
              <button type="submit" className="btn btn-cta" disabled={submitting}>
                {submitting ? 'מוסיף…' : 'הוסף'}
              </button>
            </div>
          </form>

          {formError && (
            <p role="alert" className="text-danger mt-3 text-sm">
              {formError}
            </p>
          )}
        </section>

        {rowError && (
          <p role="alert" className="text-danger mt-4 text-sm">
            {rowError}
          </p>
        )}

        {listError && (
          <p role="alert" className="text-danger mt-4 text-sm">
            {listError}
          </p>
        )}

        {categories === null && !listError && <p className="text-muted mt-6">טוען…</p>}

        {categories && categories.length === 0 && (
          <p className="text-muted mt-6">אין עדיין קטגוריות.</p>
        )}

        {categories && categories.length > 0 && (
          <table className="admin-table mt-6 w-full border-collapse text-sm">
            <caption className="sr-only">רשימת קטגוריות, לפי סדר תצוגה</caption>
            <thead>
              <tr className="border-line border-b text-start">
                <th scope="col" className="p-2 text-start">
                  תמונה
                </th>
                <th scope="col" className="p-2 text-start">
                  שם
                </th>
                <th scope="col" className="p-2 text-start">
                  קידומת
                </th>
                <th scope="col" className="p-2 text-start">
                  סטטוס
                </th>
                <th scope="col" className="p-2 text-start">
                  פעולות
                </th>
              </tr>
            </thead>
            <tbody>
              {categories.map((category, index) => (
                <tr key={category.id} className="border-line border-b">
                  <td className="p-2">
                    <div className="flex items-center gap-2">
                      <div className="bg-canvas border-line flex h-12 w-12 shrink-0 items-center justify-center overflow-hidden rounded border">
                        {category.image_url ? (
                          <img
                            src={`/${category.image_url}`}
                            alt=""
                            loading="lazy"
                            width={48}
                            height={48}
                            className="h-full w-full object-contain"
                          />
                        ) : (
                          <ImageIcon
                            size={20}
                            weight="regular"
                            aria-hidden="true"
                            className="text-muted"
                          />
                        )}
                      </div>
                      <label
                        className="flex h-11 w-11 shrink-0 cursor-pointer items-center justify-center rounded border border-line has-[:disabled]:cursor-not-allowed has-[:disabled]:opacity-40"
                        aria-label={`העלאת תמונה עבור "${category.name}"`}
                      >
                        <UploadSimple size={16} weight="regular" aria-hidden="true" />
                        <input
                          type="file"
                          accept="image/jpeg,image/png,image/webp"
                          className="sr-only"
                          disabled={uploadingImageId !== null}
                          onChange={(event) => {
                            const uploaded = event.target.files?.[0];
                            event.target.value = '';
                            if (uploaded) void handleImageUpload(category.id, uploaded);
                          }}
                        />
                      </label>
                    </div>
                  </td>
                  <td className="p-2">{category.name}</td>
                  <td className="p-2">
                    <span className="ltr-inline">{category.sku_prefix}</span>
                  </td>
                  <td className="p-2">
                    <button
                      type="button"
                      className="btn btn-secondary"
                      onClick={() => void handleToggleActive(category)}
                    >
                      {category.is_active ? 'פעילה' : 'מוסתרת'}
                    </button>
                  </td>
                  <td className="p-2">
                    <div className="flex items-center gap-1">
                      <button
                        type="button"
                        aria-label={`העבר את "${category.name}" למעלה`}
                        disabled={index === 0}
                        onClick={() => void handleMove(index, -1)}
                        className="flex h-11 w-11 items-center justify-center rounded border border-line disabled:opacity-40"
                      >
                        <ArrowUp size={16} weight="regular" aria-hidden="true" />
                      </button>
                      <button
                        type="button"
                        aria-label={`העבר את "${category.name}" למטה`}
                        disabled={index === categories.length - 1}
                        onClick={() => void handleMove(index, 1)}
                        className="flex h-11 w-11 items-center justify-center rounded border border-line disabled:opacity-40"
                      >
                        <ArrowDown size={16} weight="regular" aria-hidden="true" />
                      </button>

                      {confirmDeleteId === category.id ? (
                        <>
                          <button
                            type="button"
                            className="text-danger rounded border border-line px-2 py-1"
                            onClick={() => void handleDelete(category.id)}
                          >
                            לאשר מחיקה
                          </button>
                          <button
                            type="button"
                            className="rounded border border-line px-2 py-1"
                            onClick={() => setConfirmDeleteId(null)}
                          >
                            ביטול
                          </button>
                        </>
                      ) : (
                        <button
                          type="button"
                          aria-label="מחיקה"
                          className="flex h-11 w-11 items-center justify-center rounded border border-line"
                          onClick={() => setConfirmDeleteId(category.id)}
                        >
                          <Trash size={16} weight="regular" aria-hidden="true" />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </main>
    </>
  );
}
